import { useState } from "react";
import { Link } from "react-router-dom";
import { Hourglass, Send, Loader2, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { stripMentionIds } from "../../lib/constants";

const QUICK = ["Thanks, noted! I'll get back to you shortly.", "On it — will update you today.", "Can we talk in 10 min?"];

const waited = (m) => (m < 60 ? `${m} min` : m < 1440 ? `${Math.floor(m / 60)} h` : `${Math.floor(m / 1440)} d`);

// Clients whose last message hasn't been answered. Reply inline without
// leaving the dashboard; answered rows slide away.
export default function WaitingOnYou({ items = [], total = 0 }) {
  const [open, setOpen] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [answered, setAnswered] = useState(() => new Set());

  const send = async (w, body) => {
    const msg = (body ?? text).trim();
    if (!msg) return;
    setSending(true);
    try {
      await api.post("/whatsapp/send", { chat_id: w.chat_id, text: msg });
      setAnswered((s) => new Set(s).add(w.chat_id));
      setOpen(null);
      setText("");
      toast.success(`Replied to ${w.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <section className="bg-white rounded-xl border border-slate-200" data-testid="waiting-on-you">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between rounded-t-xl bg-amber-50/40">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-800 flex items-center gap-1.5">
          <Hourglass size={13} className="text-amber-600" /> Waiting on your reply
        </h3>
        {total > 0 && <span className="text-[11px] font-bold text-amber-800 bg-amber-100 rounded-full px-2 py-0.5 tabular-nums">{total}</span>}
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <div className="w-10 h-10 mx-auto rounded-full bg-emerald-50 grid place-items-center sw-pop"><Check size={18} className="text-emerald-600" /></div>
          <div className="text-[13px] font-semibold text-slate-800 mt-2">Nobody's waiting</div>
          <div className="text-[12px] text-slate-500">Every client message has a reply.</div>
        </div>
      ) : (
        <div>
          {items.map((w, i) => {
            const done = answered.has(w.chat_id);
            return (
              <div key={w.chat_id} className={`sw-collapse border-b last:border-b-0 border-slate-100 ${done ? "is-collapsed" : ""}`}>
                <div>
                  <div className={"px-4 py-2.5 " + (done ? "sw-exit-approve" : "sw-fade-up-clean")} style={done ? undefined : { animationDelay: `${i * 35}ms` }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[13px] font-semibold text-slate-900 truncate">{w.name}</span>
                      <span className={"ml-auto shrink-0 text-[11px] font-semibold tabular-nums " + (w.minutes >= 240 ? "text-red-600" : "text-amber-700")}>{waited(w.minutes)}</span>
                    </div>
                    <div className="text-[12px] text-slate-500 line-clamp-1 mt-0.5">{stripMentionIds(w.text) || "—"}</div>
                    {open === w.chat_id ? (
                      <div className="mt-2 sw-pop">
                        <div className="flex flex-wrap gap-1 mb-1.5">
                          {QUICK.map((q) => (
                            <button key={q} disabled={sending} onClick={() => send(w, q)}
                              className="text-[11px] px-2 py-1 rounded-full border border-slate-200 bg-white hover:border-emerald-400 hover:text-emerald-700 transition truncate max-w-full">
                              {q}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-1.5">
                          <input autoFocus value={text} onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") send(w); if (e.key === "Escape") setOpen(null); }}
                            placeholder={`Reply to ${w.name}…`}
                            className="flex-1 min-w-0 h-8 px-2.5 border border-slate-200 rounded-lg text-[12.5px] focus:border-emerald-500 outline-none" />
                          <button onClick={() => send(w)} disabled={sending || !text.trim()}
                            className="h-8 w-8 grid place-items-center rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 transition active:scale-95">
                            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-1.5 mt-1.5">
                        <button onClick={() => { setOpen(w.chat_id); setText(""); }}
                          className="h-7 px-2.5 inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11.5px] font-semibold transition active:scale-95"
                          data-testid={`waiting-reply-${w.chat_id}`}>
                          <Send size={11} /> Reply
                        </button>
                        <Link to={`/app/messages?chat=${encodeURIComponent(w.chat_id)}`}
                          className="h-7 px-2.5 inline-flex items-center gap-1 rounded-lg border border-slate-200 hover:border-slate-400 text-slate-600 text-[11.5px] font-medium transition">
                          <MessageCircle size={11} /> Open chat
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
